import { useContext, useLayoutEffect, useRef, useState } from "react";
import { createBrowserRouter, NavLink, Outlet } from "react-router";
import * as stylex from "@stylexjs/stylex";
import { DSPDisplayModeContext } from "@x1-contexts/DSPDisplayModeContext";
import { X1DarkModeContext } from "@x1-contexts/X1DarkModeContext";

import Blog from "./blog";
import BlogPost from "./blog-post";
import Home from "./home";
import Project from "./project";
import Work from "./work";

function RootLayout() {
  const { setDarkModeSetting } = useContext(X1DarkModeContext);
  const themeDisplayMode = useContext(DSPDisplayModeContext);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(65);

  useLayoutEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);

    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, []);

  return (
    <div {...stylex.props(styles.content)}>
      <div {...stylex.props(styles.contentWrapper)}>
        <div {...stylex.props(styles.borderLine, styles.borderLineLeft)} />
        <div {...stylex.props(styles.borderLine, styles.borderLineRight)} />
        <header ref={headerRef} {...stylex.props(styles.header)}>
          <div {...stylex.props(styles.headerContent)}>
            <div {...stylex.props(styles.titleContainer)}>
              <h1 {...stylex.props(styles.title)}>
                <span>alexisdelacruz</span>
                <span {...stylex.props(styles.titleDev)}>.dev</span>
              </h1>
              <div {...stylex.props(themeControlStyles.container)}>
                <button
                  {...stylex.props([
                    themeControlStyles.btn,
                    themeDisplayMode === "dark" && themeControlStyles.btnActive,
                  ])}
                  onClick={() => setDarkModeSetting("ENABLED")}
                >
                  <MoonIcon />
                </button>
                <button
                  {...stylex.props([
                    themeControlStyles.btn,
                    themeDisplayMode === "light" &&
                      themeControlStyles.btnActive,
                  ])}
                  onClick={() => setDarkModeSetting("DISABLED")}
                >
                  <SunIcon />
                </button>
              </div>
            </div>
            <nav {...stylex.props(styles.nav)}>
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  stylex.props(styles.navLink, isActive && styles.navLinkActive)
                    .className
                }
              >
                Home
              </NavLink>
              <NavLink
                to="/work"
                className={({ isActive }) =>
                  stylex.props(styles.navLink, isActive && styles.navLinkActive)
                    .className
                }
              >
                Work
              </NavLink>
              <NavLink
                to="/project"
                className={({ isActive }) =>
                  stylex.props(styles.navLink, isActive && styles.navLinkActive)
                    .className
                }
              >
                Project
              </NavLink>
              <NavLink
                to="/blog"
                className={({ isActive }) =>
                  stylex.props(styles.navLink, isActive && styles.navLinkActive)
                    .className
                }
              >
                Blog
              </NavLink>
            </nav>
          </div>
        </header>
        <main
          {...stylex.props(styles.section, styles.main)}
          style={{ paddingTop: `${headerHeight}px` }}
        >
          <Outlet />
        </main>
        <footer {...stylex.props(styles.section, styles.footer)}>
          <div {...stylex.props(styles.footerContent)}>
            <p {...stylex.props(styles.footerText)}>
              © {new Date().getFullYear()} Alexis Dela Cruz. All rights
              reserved.
            </p>
            <div {...stylex.props(styles.footerLinks)}>
              <a
                href="https://github.com/addcx1developer"
                target="_blank"
                rel="noopener noreferrer"
                {...stylex.props(styles.footerLink)}
              >
                GitHub
              </a>
              <a
                href="https://linkedin.com/in/alexis-dela-cruz-372785213/"
                target="_blank"
                rel="noopener noreferrer"
                {...stylex.props(styles.footerLink)}
              >
                LinkedIn
              </a>
              <a
                href="mailto:dcalexis3098@hotmail.com"
                {...stylex.props(styles.footerLink)}
              >
                Contact
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

function SunIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
    </svg>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    path: "/",
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: "work",
        element: <Work />,
      },
      {
        path: "project",
        element: <Project />,
      },
      {
        path: "blog",
        element: <Blog />,
      },
      {
        path: "blog/:id",
        element: <BlogPost />,
      },
    ],
  },
]);

const styles = stylex.create({
  content: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    width: "100%",
  },
  contentWrapper: {
    flex: "1",
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  section: {
    marginInline: "auto",
    maxWidth: "1200px",
    width: "100%",
  },
  header: {
    marginInline: "auto",
    backgroundColor: "var(--nav-bar-background)",
    position: "fixed",
    viewTransitionName: "main-header",
    zIndex: 10,
    borderBottomColor: "var(--border-line)",
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    left: 0,
    maxWidth: "1200px",
    right: 0,
    top: 0,
    width: "100%",
  },
  headerContent: {
    gap: "0.5rem",
    paddingBlock: "0.5rem",
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
  },
  nav: {
    gap: "0.75rem",
    display: "flex",
    fontSize: "0.9rem",
  },
  navLink: {
    borderRadius: 999,
    paddingBlock: "0.125rem",
    paddingInline: "0.25rem",
    textDecoration: "none",
    color: "var(--primary-text)",
  },
  navLinkActive: {
    color: "var(--primary-accent)",
  },
  main: {
    flex: "1",
    gap: "1rem",
    display: "flex",
    flexDirection: "column",
  },
  titleContainer: {
    position: "relative",
    width: "100%",
  },
  title: {
    margin: 0,
    color: "var(--primary-text)",
    display: "flex",
    fontSize: "1.5rem",
    justifyContent: "center",
    textAlign: "center",
  },
  titleDev: {
    color: "var(--primary-accent)",
  },
  borderLine: {
    pointerEvents: "none",
    position: "fixed",
    zIndex: 100,
    bottom: 0,
    top: 0,
  },
  borderLineLeft: {
    transform: "translateX(-600px)",
    viewTransitionName: "border-line-left",
    borderLeftColor: "var(--border-line)",
    borderLeftStyle: "solid",
    borderLeftWidth: 1,
    left: "50%",
  },
  borderLineRight: {
    transform: "translateX(600px)",
    viewTransitionName: "border-line-right",
    borderRightColor: "var(--border-line)",
    borderRightStyle: "solid",
    borderRightWidth: 1,
    left: "50%",
  },
  footer: {
    paddingBlock: "1.5rem",
    backgroundColor: "var(--nav-bar-background)",
    borderTopColor: "var(--border-line)",
    borderTopStyle: "solid",
    borderTopWidth: 1,
    marginTop: "2rem",
  },
  footerContent: {
    gap: "1rem",
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
  },
  footerText: {
    margin: 0,
    color: "var(--primary-text)",
    fontSize: "0.875rem",
    opacity: 0.7,
    textAlign: "center",
  },
  footerLinks: {
    gap: "1rem",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  footerLink: {
    textDecoration: "none",
    transition: "opacity 0.2s ease",
    color: {
      default: "var(--primary-text)",
      ":hover": "var(--primary-accent)",
    },
    fontSize: "0.875rem",
    opacity: {
      default: 0.8,
      ":hover": 1,
    },
  },
});

const themeControlStyles = stylex.create({
  container: {
    gap: "0.25rem",
    alignItems: "center",
    display: "flex",
    position: "absolute",
    transform: "translateY(-50%)",
    right: "0.5rem",
    top: "50%",
  },
  btn: {
    padding: 4,
    borderRadius: 4,
    borderStyle: "none",
    alignItems: "center",
    appearance: "none",
    backgroundColor: "transparent",
    color: "var(--primary-text)",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    opacity: 0.33,
  },
  btnActive: {
    backgroundColor: "var(--primary-accent)",
    color: "#E4E6EB",
    opacity: 1,
  },
});
